import CognitoAuthService from "src/components/login/CognitoAuthService"
import Clock from "src/general/Clock";
import StateConstants from "src/states/StateContstants"
import PersistentCookieHandler from "src/components/login/PersistentCookieHandler"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import navigationService from "src/navigation/NavigationService"
import PathRouter from "src/navigation/PathRouter"
import { clearUserEntitlements } from "src/utils/LocalDevAccess"
import posthog from "src/consent/posthog"
import { googleLogout } from "@react-oauth/google"

type CacheClearable = { clearCache: () => void }


class LoginHelper {
    loginDetails: any;
    cognitoAuthService: CognitoAuthService
    clock: Clock;
    stateHandler: EventAdaptingStateHandler;
    cookieHandler: PersistentCookieHandler;
    backendBaseApiUrl: string;
    private apiCacheClearer: CacheClearable | null = null;

    constructor(loginDetails: any, clock: Clock, stateHandler: EventAdaptingStateHandler, backendBaseApiUrl: string = '') {
        this.loginDetails = loginDetails
        this.clock = clock
        this.stateHandler = stateHandler
        this.cookieHandler = new PersistentCookieHandler(loginDetails.IS_HTTPS || false)
        this.cognitoAuthService = new CognitoAuthService({ loginDetails })
        this.backendBaseApiUrl = backendBaseApiUrl
    }

    setApiCacheClearer(clearer: CacheClearable) {
        this.apiCacheClearer = clearer
    }

    async getValidToken() {
        return await this.cognitoAuthService.getToken(this.backendBaseApiUrl)
    }

    async logout() {
        // Clears Google's auto-select grant for this browser. One Tap runs with
        // auto_select enabled, so without this the prompt would silently sign the user
        // straight back in the moment IS_LOGGED_IN flips to false — making logout, and
        // the forced logout on an expired session, impossible to escape.
        googleLogout()
        try {
            await this.cognitoAuthService.signOut()
        } catch (error) {
            console.error('LoginHelper: signOut error:', error)
        }
        this.setAuthenticationState(false)
        clearUserEntitlements(this.stateHandler)
        this.stateHandler.set(StateConstants.SAVED_VALUATIONS, null)
        this.stateHandler.set(StateConstants.PENDING_SAVE_VALUATION_PAYLOAD, null)
        this.apiCacheClearer?.clearCache()
        console.log('LoginHelper: Logout completed');
    }

    async signIn(email: string, password: string) {
        try {
            console.log('LoginHelper: Starting signIn process');
            const result = await this.cognitoAuthService.signIn(email, password)
            console.log('LoginHelper: signIn result:', result);

            if (result.success) {
                console.log('LoginHelper: SignIn successful, syncing auth state');
                await this.syncAuthenticationState();
                this.redirectToHome()
            }
            return result
        } catch (error) {
            console.error('LoginHelper: SignIn error:', error);
            throw error
        }
    }

    async signInWithoutRedirect(email: string, password: string) {
        try {
            const result = await this.cognitoAuthService.signIn(email, password)
            if (result.success) {
                await this.syncAuthenticationState()
            }
            return result
        } catch (error) {
            throw error
        }
    }

    /**
     * Exchanges a Google ID token for Cognito tokens via the backend and marks the
     * session as authenticated. Shared by the Google sign-in button and the One Tap
     * prompt; loginMethod only distinguishes the two for analytics.
     */
    async signInWithGoogleIdToken(idToken: string, loginMethod: string = 'google') {
        const response = await fetch(`${this.backendBaseApiUrl}/google_sso`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ google_id_token: idToken }),
        })

        if (!response.ok) {
            const errorData = await response.json().catch(() => ({}))
            throw new Error(errorData.error || 'Google sign-in failed')
        }

        const data = await response.json()
        this.cognitoAuthService.storeSsoTokens(data.id_token, data.access_token, data.refresh_token)
        this.setAuthenticationState(true)
        this.identifyGoogleUser(idToken, loginMethod)
        return data
    }

    private identifyGoogleUser(idToken: string, loginMethod: string) {
        try {
            const base64 = idToken.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
            const googlePayload = JSON.parse(atob(base64))
            if (googlePayload.email) {
                posthog?.identify(googlePayload.email, { email: googlePayload.email, login_method: loginMethod })
            }
        } catch {}
        posthog?.capture('user_logged_in', { login_method: loginMethod })
    }

    async signUp(email: string, password: string) {
        try {
            return await this.cognitoAuthService.signUp(email, password)
        } catch (error) {
            throw error
        }
    }

    async confirmSignUp(email: string, code: string) {
        try {
            return await this.cognitoAuthService.confirmSignUp(email, code)
        } catch (error) {
            throw error
        }
    }

    async resendConfirmationCode(email: string) {
        try {
            return await this.cognitoAuthService.resendConfirmationCode(email)
        } catch (error) {
            throw error
        }
    }

    async forgotPassword(email: string) {
        try {
            return await this.cognitoAuthService.forgotPassword(email)
        } catch (error) {
            throw error
        }
    }

    async resetPassword(email: string, code: string, newPassword: string) {
        try {
            return await this.cognitoAuthService.resetPassword(email, code, newPassword)
        } catch (error) {
            throw error
        }
    }

    setAuthenticationState(isLoggedIn: boolean) {
        this.stateHandler.set(StateConstants.IS_LOGGED_IN, isLoggedIn)
    }

    async syncAuthenticationState() {
        try {
            const amplifyAuthState = await this.cognitoAuthService.isAuthenticated();
            const currentAppState = this.stateHandler.get(StateConstants.IS_LOGGED_IN);

            if (amplifyAuthState && !currentAppState) {
                this.setAuthenticationState(true);
            } else if (!amplifyAuthState && currentAppState) {
                this.setAuthenticationState(false);
            }
        } catch (error) {
            console.error('Sync: Error syncing authentication state:', error);
            this.setAuthenticationState(false);
        }
    }

    async isLoggedIn() {
        return Boolean(await this.cognitoAuthService.getToken(this.backendBaseApiUrl))
    }

    redirectToLoginSite() {
        if (this.stateHandler) {
            this.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true);
        }
    }

    redirectToHome() {
        let savedPageState = null;
        try {
            const cookieValue = this.cookieHandler.getCookie('saved_page_state');
            if (cookieValue) {
                savedPageState = JSON.parse(cookieValue);
            }
        } catch (error) {
            // Graceful fallback if cookie parsing fails
        }

        if (!savedPageState && this.stateHandler) {
            savedPageState = this.stateHandler.get(StateConstants.CURRENT_PAGE_STATE);
        }

        if (savedPageState && savedPageState.fullUrl) {
            const currentTime = this.clock.get_current_timestamp_in_millis();
            const timeDiff = currentTime - savedPageState.timestamp;
            const oneHourInMillis = 60 * 60 * 1000;

            if (timeDiff < oneHourInMillis) {
                this.cookieHandler.deleteCookie('saved_page_state');
                if (this.stateHandler) {
                    this.stateHandler.set(StateConstants.CURRENT_PAGE_STATE, null);
                }
                window.location.href = savedPageState.fullUrl;
                return;
            } else {
                this.cookieHandler.deleteCookie('saved_page_state');
            }
        }

        const url = window.location.href;
        const urlObj = new URL(url);
        const redirectParam = urlObj.searchParams.get('redirect');

        if (redirectParam) {
            if (redirectParam.startsWith('http://') || redirectParam.startsWith('https://')) {
                window.location.href = redirectParam;
            } else {
                navigationService.navigateTo(redirectParam);
            }
        } else {
            navigationService.navigateToHome();
        }
    }
}

export default LoginHelper
